import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/*
 * ServiceRadiusMap — stylized Puget Sound coverage map.
 * Pure SVG, drawn in the site's blueprint/engineered visual language:
 * water band, fine grid, mono spec labels, two dashed 60-mile radius
 * circles around the Federal Way HQ (copper) and Snohomish branch (steel).
 * Stylized for clarity — distances approximate.
 */

const MONO = "'IBM Plex Mono', monospace";

const CITY_DOTS = [
  { name: 'Seattle', x: 238, y: 292, anchor: 'start', dx: 8, dy: 4 },
  { name: 'Bellevue', x: 318, y: 300, anchor: 'start', dx: 8, dy: 4 },
  { name: 'Everett', x: 262, y: 142, anchor: 'end', dx: -8, dy: 4 },
  { name: 'Tacoma', x: 200, y: 458, anchor: 'end', dx: -8, dy: 4 },
  { name: 'Olympia', x: 148, y: 592, anchor: 'start', dx: 8, dy: 4 },
];


/* ----------------------- map building blocks ----------------------- */
const RadiusRing = ({ cx, cy, color, tickTo, labelAt, labelColor }) => (
  <g>
    <circle cx={cx} cy={cy} r="206" fill={color} fillOpacity="0.05" stroke={color} strokeWidth="1.75" strokeDasharray="7 6" />
    <line x1={cx} y1={cy} x2={tickTo[0]} y2={tickTo[1]} stroke={color} strokeOpacity="0.6" strokeWidth="1" strokeDasharray="3 4" />
    <text x={labelAt[0]} y={labelAt[1]} fontFamily={MONO} fontSize="16" letterSpacing="2.5" fill={labelColor}>60 MI</text>
  </g>
);

const OfficeMarker = ({ cx, cy, color, lineColor, labelColor, label, sublabel, labelX, labelY }) => (
  <g>
    <circle cx={cx} cy={cy} r="15" fill={color} fillOpacity="0.18" />
    <circle cx={cx} cy={cy} r="7" fill={color} stroke="#FFFCF6" strokeWidth="1.75" />
    <line x1={cx + 11} y1={cy} x2={labelX - 4} y2={labelY + 4} stroke={lineColor} strokeWidth="1" />
    <text x={labelX} y={labelY} fontFamily={MONO} fontSize="17" fontWeight="600" letterSpacing="1.5" fill={labelColor}>{label}</text>
    <text x={labelX} y={labelY + 18} fontFamily={MONO} fontSize="13" letterSpacing="1.5" fill="#61676E">{sublabel}</text>
  </g>
);

const CityDots = () => (
  <>
    {CITY_DOTS.map((c) => (
      <g key={c.name}>
        <circle cx={c.x} cy={c.y} r="4.5" fill="#61676E" />
        <text x={c.x + c.dx} y={c.y + c.dy} textAnchor={c.anchor} fontFamily={MONO} fontSize="14.5" letterSpacing="1.5" fill="#61676E">
          {c.name.toUpperCase()}
        </text>
      </g>
    ))}
  </>
);

const MapFooterBar = () => (
  <div className="px-5 py-4 border-t border-[#D7D2C8] bg-[#FFFCF6] flex flex-wrap items-center justify-between gap-3">
    <div className="flex flex-wrap items-center gap-4">
      <span className="inline-flex items-center gap-2 text-xs text-[#454B52]">
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#D07A4A]" /> Federal Way HQ
      </span>
      <span className="inline-flex items-center gap-2 text-xs text-[#454B52]">
        <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#3E6F8E]" /> Snohomish Branch
      </span>
    </div>
    <Link to="/service-areas" className="inline-flex items-center gap-1.5 text-xs font-medium text-[#3E6F8E] hover:underline decoration-[rgba(208,122,74,0.85)] decoration-2 underline-offset-4 max-md:min-h-11" data-testid="radius-map-areas-link">
      Every city we serve <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  </div>
);

export const ServiceRadiusMap = ({ className = '' }) => (
  <div className={`idb-card idb-brackets overflow-hidden flex flex-col ${className}`} data-testid="service-radius-map">
    <div className="px-5 pt-5 pb-3 flex items-center justify-between gap-3">
      <p className="idb-spec">Coverage — 60 mi radius per office</p>
      <span className="font-mono-idb text-[10px] max-md:text-xs uppercase tracking-[0.18em] text-[#61676E]">Puget Sound, WA</span>
    </div>
    <div className="relative flex-1 min-h-[320px]">
      <svg
        viewBox="-60 0 680 640"
        role="img"
        aria-label="Stylized map of Puget Sound showing a 60-mile service radius around the Federal Way headquarters and the Snohomish branch"
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <pattern id="srm-grid" width="28" height="28" patternUnits="userSpaceOnUse">
            <path d="M 28 0 L 0 0 0 28" fill="none" stroke="#121417" strokeOpacity="0.05" strokeWidth="1" />
          </pattern>
        </defs>

        {/* Land (oversized so letterboxed areas render as seamless map) */}
        <rect x="-700" y="-700" width="1960" height="2040" fill="#F4F1EA" />
        <rect x="-700" y="-700" width="1960" height="2040" fill="url(#srm-grid)" />

        {/* Puget Sound water band (stylized, extended beyond frame) */}
        {/* Single-line path data on purpose: the HTML serializer collapses attribute whitespace, so a
            multi-line literal would differ from the pre-rendered markup and trip hydration. */}
        <path
          d="M 118 -300 L 132 -60 C 150 20, 108 132, 134 198 C 154 252, 104 318, 130 388 C 150 444, 96 502, 112 568 C 118 596, 102 620, 92 660 L 78 940 L 168 940 C 162 640, 176 578, 166 540 C 154 494, 206 452, 192 402 C 180 362, 226 312, 212 266 C 200 228, 246 176, 232 130 C 222 94, 250 44, 242 -40 L 236 -300 Z"
          fill="#7FA7B8"
          fillOpacity="0.28"
          stroke="#3E6F8E"
          strokeOpacity="0.35"
          strokeWidth="1.5"
        />
        {/* Whidbey Island (stylized) */}
        <ellipse cx="188" cy="70" rx="16" ry="42" fill="#F4F1EA" stroke="#3E6F8E" strokeOpacity="0.3" strokeWidth="1" transform="rotate(18 188 70)" />

        {/* 60-mile radius rings */}
        <RadiusRing cx={252} cy={408} color="#D07A4A" tickTo={[392, 556]} labelAt={[338, 500]} labelColor="#B4602F" />

        <RadiusRing cx={332} cy={152} color="#3E6F8E" tickTo={[476, 290]} labelAt={[420, 240]} labelColor="#355F79" />

        {/* Anchor city dots */}
        {CITY_DOTS.map((c) => (
          <g key={c.name}>
            <circle cx={c.x} cy={c.y} r="4.5" fill="#61676E" />
            <text x={c.x + c.dx} y={c.y + c.dy} textAnchor={c.anchor} fontFamily={MONO} fontSize="14.5" letterSpacing="1.5" fill="#61676E">
              {c.name.toUpperCase()}
            </text>
          </g>
        ))}

        <OfficeMarker cx={252} cy={408} color="#D07A4A" lineColor="#B4602F" labelColor="#8A4622" label="FEDERAL WAY HQ" sublabel="SOUTH SOUND" labelX={300} labelY={382} />

        <OfficeMarker cx={332} cy={152} color="#3E6F8E" lineColor="#355F79" labelColor="#2A4B60" label="SNOHOMISH BRANCH" sublabel="NORTH SOUND" labelX={380} labelY={126} />

        {/* North arrow */}
        <g transform="translate(586, 40)">
          <line x1="0" y1="14" x2="0" y2="-10" stroke="#61676E" strokeWidth="1.25" />
          <path d="M 0 -14 L -4.5 -5 L 4.5 -5 Z" fill="#61676E" />
          <text x="0" y="30" textAnchor="middle" fontFamily={MONO} fontSize="13" letterSpacing="1" fill="#61676E">N</text>
        </g>

        {/* Scale note */}
        <text x="-44" y="624" fontFamily={MONO} fontSize="12" letterSpacing="1.5" fill="#9A968C">STYLIZED — DISTANCES APPROXIMATE</text>
      </svg>
    </div>
    <MapFooterBar />
  </div>
);
